import { useCallback, useEffect, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError } from "@pontimed/api-client";
import { createFileRoute } from "@tanstack/react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAdminFetch } from "@/hooks/use-admin-fetch";

/**
 * Admin → Feedback (AI triage queue).
 *
 * Port of `src/app/admin/feedback/page.tsx` (legacy NextJS, 608 LOC) per D21.
 *
 * Wiring (apps/api endpoints — confirmed live per T1 inventory Day 3-4):
 *   - GET    /api/admin/feedback?status=...                 → FeedbackItem[]
 *   - GET    /api/admin/feedback/counts                     → counts map
 *   - PATCH  /api/admin/feedback (id, status, resolution?)  → update
 *   - DELETE /api/admin/feedback (id)                       → remove
 *   - POST   /api/admin/feedback/generate-prompt (id)       → DevPromptResult
 *
 * Encrypted screenshots (Phase 2 Step 2): `screenshotDeks[i]` populated →
 * bytes come from `GET /api/admin/feedback/:id/screenshot/:i` (auth + decrypt).
 * That endpoint cannot be used as an `<img src>`: on admin.pontimed.app a
 * relative `/api/...` resolves to the SPA shell (the API lives on
 * api.pontimed.app) and an image request carries none of the `X-Admin-DB`
 * header the admin fetch interceptor injects. So the blobs are streamed
 * through `adminFetch` and rendered as object URLs.
 */

interface FeedbackItem {
  id: string;
  type: string;
  description: string;
  userId: string | null;
  userType: string | null;
  userEmail: string | null;
  pageUrl: string | null;
  device: { userAgent?: string; screenSize?: string; ip?: string } | null;
  consoleErrors: unknown[] | null;
  networkErrors: unknown[] | null;
  performance: Record<string, unknown> | null;
  screenshots: string[];
  screenshotDeks?: string[];
  aiCategory: string | null;
  aiPriority: string | null;
  aiComponent: string | null;
  aiSummary: string | null;
  aiSuggestedFix: string | null;
  aiSentiment: string | null;
  status: string;
  assignedTo: string | null;
  resolution: string | null;
  adminNotes: string | null;
  userNotified: boolean;
  createdAt: string;
}

interface DevPromptResult {
  prompt: string;
  filesAffected: string[];
  estimatedEffort: string;
  riskLevel: string;
  implementationSteps: string[];
  localScreenshots?: string[];
}

const priorityColors: Record<string, string> = {
  critical: "bg-red-100 text-red-700",
  high: "bg-orange-100 text-orange-700",
  medium: "bg-yellow-100 text-yellow-700",
  low: "bg-gray-100 text-gray-600",
};

const statusColors: Record<string, string> = {
  new: "bg-blue-100 text-blue-700",
  triaged: "bg-purple-100 text-purple-700",
  in_progress: "bg-amber-100 text-amber-700",
  resolved: "bg-green-100 text-green-700",
  wont_fix: "bg-gray-100 text-gray-500",
  duplicate: "bg-gray-100 text-gray-400",
};

const sentimentIcons: Record<string, string> = {
  positive: "😊",
  negative: "😞",
  neutral: "😐",
  frustrated: "😤",
};

const FILTERS = ["all", "new", "triaged", "in_progress", "resolved"] as const;
type Filter = (typeof FILTERS)[number];

export const Route = createFileRoute("/_authenticated/feedback")({
  component: FeedbackPage,
});

function FeedbackPage() {
  const { t } = useLingui();
  const { adminFetch } = useAdminFetch();
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({
    all: 0,
    new: 0,
    triaged: 0,
    in_progress: 0,
    resolved: 0,
  });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>("all");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [lightboxImg, setLightboxImg] = useState<string | null>(null);
  const [replyingId, setReplyingId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");
  const [screenshotUrls, setScreenshotUrls] = useState<Record<string, string>>({});
  const [replySending, setReplySending] = useState(false);
  const [generatingPromptId, setGeneratingPromptId] = useState<string | null>(null);
  const [devPrompts, setDevPrompts] = useState<Record<string, DevPromptResult>>({});
  const [promptCopied, setPromptCopied] = useState<string | null>(null);
  const [promptError, setPromptError] = useState<string | null>(null);

  const typeLabels: Record<string, string> = {
    bug: t`Technical issue`,
    feature_request: t`New idea`,
    question: t`Question`,
    praise: t`Praise`,
    complaint: t`Complaint`,
    content_issue: t`Bad data`,
  };

  const refreshCounts = useCallback(async () => {
    try {
      const data = await adminFetch.get<Record<string, number>>("/api/admin/feedback/counts");
      setCounts(data);
    } catch {
      /* ignore */
    }
  }, [adminFetch]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const path =
          filter !== "all" ? `/api/admin/feedback?status=${filter}` : "/api/admin/feedback";
        const data = await adminFetch.get<FeedbackItem[]>(path);
        if (!cancelled) setFeedback(data);
      } catch {
        if (!cancelled) setFeedback([]);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [adminFetch, filter]);

  // ── Encrypted screenshots → object URLs ─────────────────────────────────
  // Re-runs whenever the list reloads or the Local↔Prod toggle flips
  // (`adminFetch` identity changes), so previews always match the rows shown.
  useEffect(() => {
    const wanted = feedback.flatMap((f) =>
      (f.screenshots ?? []).flatMap((_src, i) =>
        f.screenshotDeks?.[i]
          ? [{ key: `${f.id}:${i}`, path: `/api/admin/feedback/${f.id}/screenshot/${i}` }]
          : [],
      ),
    );
    if (wanted.length === 0) {
      setScreenshotUrls({});
      return;
    }
    let cancelled = false;
    const created: string[] = [];
    void (async () => {
      const resolved: Record<string, string> = {};
      for (const { key, path } of wanted) {
        try {
          const res = await adminFetch.raw(path, { method: "GET" });
          if (!res.ok) continue;
          const objectUrl = URL.createObjectURL(await res.blob());
          created.push(objectUrl);
          resolved[key] = objectUrl;
        } catch {
          // Leave unresolved — the thumbnail renders a skeleton placeholder.
        }
      }
      if (cancelled) {
        created.forEach((u) => URL.revokeObjectURL(u));
        return;
      }
      setScreenshotUrls(resolved);
    })();
    return () => {
      cancelled = true;
      created.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [feedback, adminFetch]);

  useEffect(() => {
    refreshCounts();
  }, [refreshCounts]);

  const updateStatus = async (id: string, status: string) => {
    try {
      await adminFetch.patch("/api/admin/feedback", { id, status });
      setFeedback((prev) => prev.map((f) => (f.id === id ? { ...f, status } : f)));
      refreshCounts();
    } catch {
      /* ignore */
    }
  };

  const deleteFeedback = async (id: string) => {
    if (!confirm(t`Delete this feedback?`)) return;
    try {
      await adminFetch.raw("/api/admin/feedback", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });
      setFeedback((prev) => prev.filter((f) => f.id !== id));
      refreshCounts();
    } catch {
      /* ignore */
    }
  };

  const sendReply = async (id: string, resolution: string) => {
    setReplySending(true);
    try {
      await adminFetch.patch("/api/admin/feedback", { id, status: "resolved", resolution });
      setFeedback((prev) =>
        prev.map((f) =>
          f.id === id ? { ...f, status: "resolved", resolution, userNotified: true } : f,
        ),
      );
      setReplyingId(null);
      setReplyText("");
      refreshCounts();
    } catch {
      /* ignore */
    }
    setReplySending(false);
  };

  const generateDevPrompt = useCallback(
    async (id: string) => {
      setGeneratingPromptId(id);
      setPromptError(null);
      try {
        const data = await adminFetch.post<DevPromptResult>("/api/admin/feedback/generate-prompt", {
          id,
        });
        setDevPrompts((prev) => ({ ...prev, [id]: data }));
      } catch (err) {
        if (err instanceof ApiError) {
          const msg =
            (err.body as { error?: string } | undefined)?.error ??
            err.message ??
            "Failed to generate prompt";
          setPromptError(`Error ${err.status}: ${msg}`);
        } else if (err instanceof Error) {
          setPromptError(err.message);
        } else {
          setPromptError("Network error");
        }
      }
      setGeneratingPromptId(null);
    },
    [adminFetch],
  );

  const copyPrompt = useCallback(async (id: string, text: string) => {
    await navigator.clipboard.writeText(text);
    setPromptCopied(id);
    setTimeout(() => setPromptCopied(null), 2000);
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">
          <Trans>Feedback</Trans>
        </h1>
        <p className="text-muted-foreground text-sm">
          <Trans>User feedback with AI triage — {counts.all} total</Trans>
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`rounded-lg px-3 py-1 text-xs font-medium transition-colors ${
              filter === s ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
            }`}
          >
            {s === "all" ? "All" : s.replace("_", " ")} ({counts[s] || 0})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-400">
          <Trans>Loading...</Trans>
        </div>
      ) : feedback.length === 0 ? (
        <div className="py-12 text-center text-gray-400">
          <Trans>No feedback yet</Trans>
        </div>
      ) : (
        <div className="space-y-2">
          {feedback.map((f) => (
            <div
              key={f.id}
              className="overflow-hidden rounded-xl border bg-white p-4 dark:bg-gray-900"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge className={`text-[0.625rem] ${statusColors[f.status] || ""}`}>
                      {f.status}
                    </Badge>
                    {f.aiPriority && (
                      <Badge className={`text-[0.625rem] ${priorityColors[f.aiPriority] || ""}`}>
                        {f.aiPriority}
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-[0.625rem]">
                      {typeLabels[f.type] || f.type}
                    </Badge>
                    {f.aiComponent && (
                      <Badge variant="outline" className="text-[0.625rem]">
                        {f.aiComponent}
                      </Badge>
                    )}
                    {f.aiSentiment && (
                      <span className="text-sm">{sentimentIcons[f.aiSentiment] || ""}</span>
                    )}
                  </div>

                  {f.aiSummary && <p className="mt-1 text-sm font-medium">{f.aiSummary}</p>}

                  <p className="text-muted-foreground mt-1 text-sm">{f.description}</p>

                  {f.aiSuggestedFix && (
                    <div className="mt-1.5 rounded bg-amber-50 px-2 py-1 text-xs text-amber-700 dark:bg-amber-950/20 dark:text-amber-400">
                      💡 {f.aiSuggestedFix}
                    </div>
                  )}

                  {f.screenshots?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {f.screenshots.map((src, i) => {
                        const dek = f.screenshotDeks?.[i];
                        const displaySrc = dek ? screenshotUrls[`${f.id}:${i}`] : src;
                        return (
                          <button
                            key={i}
                            disabled={!displaySrc}
                            onClick={() => displaySrc && setLightboxImg(displaySrc)}
                            className="border-border overflow-hidden rounded-md border transition-opacity hover:opacity-80 disabled:cursor-progress"
                          >
                            {displaySrc ? (
                              <img
                                src={displaySrc}
                                alt={`Screenshot ${i + 1}`}
                                className="h-16 w-24 object-cover"
                              />
                            ) : (
                              <div className="bg-muted h-16 w-24 animate-pulse" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  <div className="mt-2 flex flex-wrap items-center gap-2 text-[0.625rem] text-gray-400">
                    {f.userType && <span>{f.userType}</span>}
                    {f.userEmail && <span>· {f.userEmail}</span>}
                    {f.pageUrl && <span>· {f.pageUrl}</span>}
                    <span>· {new Date(f.createdAt).toLocaleDateString()}</span>
                    {f.device?.ip && <span>· IP: {f.device.ip}</span>}
                    <button
                      onClick={() => setExpandedId(expandedId === f.id ? null : f.id)}
                      className="text-muted-foreground hover:text-foreground ml-auto text-[0.625rem] underline"
                    >
                      {expandedId === f.id ? <Trans>▲ Less</Trans> : <Trans>▼ More</Trans>}
                    </button>
                  </div>

                  {expandedId === f.id && f.device && (
                    <div className="bg-muted/50 mt-2 space-y-0.5 rounded-lg px-3 py-2 text-[0.6875rem]">
                      {f.device.screenSize && (
                        <p>
                          <span className="text-muted-foreground">
                            <Trans>Resolution:</Trans>
                          </span>{" "}
                          {f.device.screenSize}
                        </p>
                      )}
                      {f.device.ip && (
                        <p>
                          <span className="text-muted-foreground">IP:</span> {f.device.ip}
                        </p>
                      )}
                      {f.device.userAgent && (
                        <p className="break-all">
                          <span className="text-muted-foreground">UA:</span> {f.device.userAgent}
                        </p>
                      )}
                    </div>
                  )}

                  {f.userId && f.status !== "resolved" && (
                    <div className="mt-2">
                      {replyingId === f.id ? (
                        <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-2.5 dark:border-emerald-800 dark:bg-emerald-950/30">
                          <p className="mb-1.5 text-[0.625rem] font-medium text-emerald-700 dark:text-emerald-400">
                            <Trans>Reply → user notification</Trans>
                          </p>
                          <textarea
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder={t`Write a reply...`}
                            rows={2}
                            className="border-input bg-background focus:ring-primary/30 focus:border-primary mb-2 w-full rounded-md border px-2.5 py-1.5 text-xs outline-none focus:ring-2 focus:ring-inset"
                          />
                          <div className="flex flex-wrap gap-1.5">
                            <button
                              onClick={() =>
                                sendReply(f.id, t`Your feedback was reviewed and resolved. Thanks!`)
                              }
                              disabled={replySending}
                              className="rounded-md bg-emerald-100 px-2.5 py-1 text-[0.625rem] font-medium text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900 dark:text-emerald-300"
                            >
                              <Trans>✓ Resolved + Thanks</Trans>
                            </button>
                            <button
                              onClick={() =>
                                sendReply(
                                  f.id,
                                  t`We received your feedback and are working on it. Thanks!`,
                                )
                              }
                              disabled={replySending}
                              className="rounded-md bg-blue-100 px-2.5 py-1 text-[0.625rem] font-medium text-blue-700 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-300"
                            >
                              <Trans>📨 Acknowledge receipt</Trans>
                            </button>
                            {replyText.trim() && (
                              <button
                                onClick={() => sendReply(f.id, replyText)}
                                disabled={replySending}
                                className="bg-primary text-primary-foreground rounded-md px-2.5 py-1 text-[0.625rem] font-medium"
                              >
                                {replySending ? "..." : <Trans>Send custom reply</Trans>}
                              </button>
                            )}
                            <button
                              onClick={() => {
                                setReplyingId(null);
                                setReplyText("");
                              }}
                              className="text-muted-foreground hover:text-foreground rounded-md px-2.5 py-1 text-[0.625rem]"
                            >
                              <Trans>Cancel</Trans>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setReplyingId(f.id)}
                          className="flex items-center gap-1 rounded-md bg-emerald-50 px-2.5 py-1 text-[0.625rem] font-medium text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-400"
                        >
                          <Trans>💬 Reply + notify</Trans>
                        </button>
                      )}
                    </div>
                  )}
                  {f.resolution && (
                    <div className="mt-2 rounded-lg bg-emerald-50/50 px-2.5 py-1.5 text-[0.6875rem] text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400">
                      ✓ {f.resolution}
                    </div>
                  )}
                  {f.userNotified && (
                    <p className="mt-1 text-[0.625rem] text-emerald-600">
                      <Trans>✓ User has been notified</Trans>
                    </p>
                  )}

                  {(f.consoleErrors && f.consoleErrors.length > 0) ||
                  (f.networkErrors && f.networkErrors.length > 0) ? (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {f.consoleErrors && f.consoleErrors.length > 0 && (
                        <Badge variant="outline" className="text-[0.625rem] text-red-600">
                          <Trans>{f.consoleErrors.length} console error(s)</Trans>
                        </Badge>
                      )}
                      {f.networkErrors && f.networkErrors.length > 0 && (
                        <Badge variant="outline" className="text-[0.625rem] text-orange-600">
                          <Trans>{f.networkErrors.length} network error(s)</Trans>
                        </Badge>
                      )}
                    </div>
                  ) : null}

                  {f.performance && Object.keys(f.performance).length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-2 text-[0.625rem] text-gray-400">
                      {Object.entries(f.performance).map(([key, val]) => (
                        <span key={key}>
                          {key}: {String(val)}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="mt-3 border-t border-dashed border-gray-200 pt-2 dark:border-gray-700">
                    {devPrompts[f.id] ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-[0.6875rem] font-semibold text-indigo-700 dark:text-indigo-400">
                            <Trans>Dev Prompt</Trans>
                          </p>
                          <div className="flex gap-1.5">
                            <button
                              onClick={() => {
                                const local = devPrompts[f.id].localScreenshots;
                                const screenshots =
                                  local && local.length > 0
                                    ? `\n\n## Screenshots (local files — use Read tool to view)\n${local
                                        .map((p, i) => `- Screenshot ${i + 1}: ${p}`)
                                        .join(
                                          "\n",
                                        )}\n\nIMPORTANT: Before implementing, use the Read tool on each screenshot path above to see what the user reported.`
                                    : "";
                                copyPrompt(f.id, devPrompts[f.id].prompt + screenshots);
                              }}
                              className="rounded-md bg-indigo-100 px-2 py-0.5 text-[0.625rem] font-medium text-indigo-700 hover:bg-indigo-200 dark:bg-indigo-900 dark:text-indigo-300"
                            >
                              {promptCopied === f.id ? (
                                <Trans>✓ Copied!</Trans>
                              ) : (
                                <Trans>Copy prompt</Trans>
                              )}
                            </button>
                            <button
                              onClick={() => generateDevPrompt(f.id)}
                              className="rounded-md bg-gray-100 px-2 py-0.5 text-[0.625rem] text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400"
                            >
                              <Trans>Regenerate</Trans>
                            </button>
                          </div>
                        </div>
                        <pre className="max-h-48 overflow-auto rounded-lg bg-gray-900 p-3 text-[0.6875rem] leading-relaxed break-words whitespace-pre-wrap text-gray-100 dark:bg-gray-950">
                          {devPrompts[f.id].prompt}
                        </pre>
                        <div className="flex flex-wrap gap-1.5">
                          <Badge
                            className={`text-[0.625rem] ${
                              devPrompts[f.id].riskLevel === "high"
                                ? "bg-red-100 text-red-700"
                                : devPrompts[f.id].riskLevel === "medium"
                                  ? "bg-yellow-100 text-yellow-700"
                                  : "bg-green-100 text-green-700"
                            }`}
                          >
                            <Trans>Risk:</Trans> {devPrompts[f.id].riskLevel}
                          </Badge>
                          <Badge className="bg-blue-100 text-[0.625rem] text-blue-700">
                            <Trans>Effort:</Trans> {devPrompts[f.id].estimatedEffort}
                          </Badge>
                        </div>
                        {devPrompts[f.id].filesAffected.length > 0 && (
                          <div className="text-[0.625rem] break-words text-gray-500">
                            <span className="font-medium">
                              <Trans>Files:</Trans>
                            </span>{" "}
                            {devPrompts[f.id].filesAffected.join(", ")}
                          </div>
                        )}
                        {devPrompts[f.id].implementationSteps.length > 0 && (
                          <ol className="ml-4 list-decimal text-[0.625rem] break-words text-gray-600 dark:text-gray-400">
                            {devPrompts[f.id].implementationSteps.map((step, i) => (
                              <li key={i}>{step}</li>
                            ))}
                          </ol>
                        )}
                        {devPrompts[f.id].localScreenshots &&
                          devPrompts[f.id].localScreenshots!.length > 0 && (
                            <div className="mt-1.5 rounded-md bg-emerald-50 px-2.5 py-1.5 text-[0.625rem] text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400">
                              <span className="font-medium">
                                <Trans>Screenshots downloaded locally:</Trans>
                              </span>
                              <ul className="mt-0.5 ml-3 list-disc break-all">
                                {devPrompts[f.id].localScreenshots!.map((p, i) => (
                                  <li key={i}>{p}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                      </div>
                    ) : (
                      <button
                        onClick={() => generateDevPrompt(f.id)}
                        disabled={generatingPromptId === f.id}
                        className="flex items-center gap-1.5 rounded-md bg-indigo-50 px-3 py-1.5 text-[0.6875rem] font-medium text-indigo-700 hover:bg-indigo-100 disabled:opacity-50 dark:bg-indigo-950/40 dark:text-indigo-400"
                      >
                        {generatingPromptId === f.id ? (
                          <>
                            <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-indigo-400 border-t-transparent" />
                            <Trans>Generating prompt...</Trans>
                          </>
                        ) : (
                          <Trans>⚡ Generate Dev Prompt</Trans>
                        )}
                      </button>
                    )}
                    {promptError && generatingPromptId === null && (
                      <p className="mt-1.5 text-[0.6875rem] text-red-600">{promptError}</p>
                    )}
                  </div>
                </div>

                <div className="flex shrink-0 flex-col gap-1">
                  <select
                    value={f.status}
                    onChange={(e) => updateStatus(f.id, e.target.value)}
                    className="rounded border px-2 py-1 text-xs"
                  >
                    <option value="new">{t`New`}</option>
                    <option value="triaged">{t`Triaged`}</option>
                    <option value="in_progress">{t`In Progress`}</option>
                    <option value="resolved">{t`Resolved`}</option>
                    <option value="wont_fix">{t`Won't Fix`}</option>
                    <option value="duplicate">{t`Duplicate`}</option>
                  </select>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-xs text-red-500"
                    onClick={() => deleteFeedback(f.id)}
                  >
                    <Trans>Delete</Trans>
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {lightboxImg && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setLightboxImg(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300"
            onClick={() => setLightboxImg(null)}
            aria-label={t`Close`}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
          <img
            src={lightboxImg}
            alt="Full screenshot"
            className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
